/**
 * Data layer for MapView.jsx via GET /search, which (unlike
 * /properties/listings) includes per-property latitude/longitude needed for
 * map pins. Normalizes the endpoint's raw snake_case shape into the field
 * names MapView's existing markers/cards already render.
 */

import { useState, useCallback, useEffect } from 'react';
import { rawRequest } from '../services/apiClient';

const buildQueryString = (filters) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') return;
        if (Array.isArray(value)) {
            if (value.length > 0) params.append(key, value.join(','));
        } else {
            params.append(key, value.toString());
        }
    });
    return params.toString();
};

const normalizeProperty = (p) => ({
    id: p.id,
    title: p.name,
    price: p.rent_amount,
    type: p.property_type,
    bedrooms: p.bedrooms,
    bathrooms: p.bathrooms,
    area: p.area_sqft,
    location: p.exact_address || '',
    coordinates: (p.latitude != null && p.longitude != null) ? [p.latitude, p.longitude] : null,
    image: p.primary_image,
    features: [], // /search doesn't return a structured amenities list
    description: p.description,
    contact: {
        phone: p.landlord?.phone || '',
        email: ''
    },
    agent: {
        name: p.landlord?.name || 'Rent Utopia',
        company: ''
    }
});

export const useMapProperties = (initialFilters = {}) => {
    const [filters, setFilters] = useState(initialFilters);
    const [properties, setProperties] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchProperties = useCallback(async (activeFilters) => {
        setLoading(true);
        setError(null);
        try {
            const query = buildQueryString(activeFilters);
            const data = await rawRequest(`/search?${query}`);
            setProperties((data.properties || []).map(normalizeProperty));
        } catch (err) {
            setError(err.message);
            setProperties([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchProperties(filters);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const updateFilters = useCallback((newFilters) => {
        setFilters(newFilters);
        fetchProperties(newFilters);
    }, [fetchProperties]);

    return {
        properties,
        loading,
        error,
        filters,
        updateFilters,
        refetch: () => fetchProperties(filters)
    };
};
